import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import {useTranslation} from 'react-i18next'
import React, {Fragment, useEffect, useState} from 'react'
import {
  getLicenseSearchByOrg,
  OrgByLicense,
  OrgByLicenseDetail,
  OrgByLicenseInquiryType,
  SearchOrgByLicenseRequest,
} from 'api/productApi'
import useProductOperationOrganizationStyles from 'pages/ProductOperationOrganization/style'
import {
  ACTIVE_PRODUCT,
  LIST_TABS,
  initialPayload,
  ProductOperationAgency,
  LIST_COLUMN,
  LIST_COLUMN_V1,
  LICENSE_STATUS,
  LicenseStatus,
} from 'pages/ProductOperationOrganization/constant'
import TableToolbar from 'pages/ProductOperationOrganization/Component/TableToolbar'
import {PAYMENT_TYPE_BY_ORG, PaymentTypeOrg} from 'pages/ProductsPages/constant'
import {getCountryFromCountryCode} from 'helpers/uiHelper'
import {AppLoading} from 'AppLoading'
import CardWithTitle from 'components/atoms/CardWithTitle'
import Pagination from 'components/molcules/Pagination'
import DialogActiveProduct from 'pages/ProductOperationOrganization/Component/DialogActiveProduct'
import {navigate} from 'AppHistory'
import {downloadCsv} from 'helpers/csvHelper'
import {getLanguage} from 'api/storageApi'

const ProductOperationOrganizationList = () => {
  const classes = useProductOperationOrganizationStyles()
  const {t} = useTranslation()
  const [activeTabs, setActiveTabs] = useState<number>(ACTIVE_PRODUCT)
  const [loading, setLoading] = useState(false)
  const [selectedLicense, setSelectedLicense] = useState<{
    orgName: string
    productName: string
    licenseId: string
    statusLicense: LicenseStatus
  }>({
    orgName: '',
    productName: '',
    licenseId: '',
    statusLicense: 'ACTIVE',
  })
  const [activeDialog, setActiveDialog] = useState<boolean>(false)
  const [pagePayload, setPagePayload] =
    useState<SearchOrgByLicenseRequest>(initialPayload)
  const [listOrgResponse, setListOrgResponse] =
    useState<OrgByLicenseInquiryType>()

  const handleRedirectToDetail = (orgId: string) => {
    navigate(
      `/products-operation-organization/products-institution-operation-history/${orgId}`,
    )
  }

  const getLicenseMethodType = (type: PaymentTypeOrg) => {
    if (type === PAYMENT_TYPE_BY_ORG.PREPAID_ONCE) {
      return t('ISubscription')
    }
    if (type === PAYMENT_TYPE_BY_ORG.PREPAID_MONTHLY) {
      return t('IPrepaid')
    }
    if (type === PAYMENT_TYPE_BY_ORG.MANUAL) {
      return t('IManual')
    }
    return ''
  }

  const getLicenseStatus = (status: string) => {
    switch (status) {
      case LICENSE_STATUS.ACTIVE:
        return t('IInOperation')
      case LICENSE_STATUS.INACTIVE:
        return t('IOnPause')
      case LICENSE_STATUS.DESTROYED:
        return t('IDestroyed')
      default:
        return ''
    }
  }

  const onChangeProductStatus = (id: number) => {
    setActiveTabs(id)
  }

  const isCheckedCheckbox = (value: string) => {
    return pagePayload[value as keyof SearchOrgByLicenseRequest] === true
  }

  const init = async () => {
    try {
      setLoading(true)
      const orgRes = await getLicenseSearchByOrg({
        ...pagePayload,
        lang: getLanguage(),
      })
      setListOrgResponse(orgRes.data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  type SearchSelectStringKey =
    | 'orgType'
    | 'paymentType'
    | 'searchValueType'
    | 'searchValue'

  const isSearchSelectStringKey = (
    key: string,
  ): key is SearchSelectStringKey => {
    return [
      'orgType',
      'paymentType',
      'searchValueType',
      'searchValue',
    ].includes(key)
  }

  const handleSearchSelect = ({key, value}: {key: string; value: string}) => {
    if (key === 'domestic') {
      setPagePayload({
        ...pagePayload,
        domestic: value === 'Korea',
      })
      return
    }

    if (isSearchSelectStringKey(key)) {
      setPagePayload({
        ...pagePayload,
        [key]: value,
      })
    }
  }

  const onChangeCheckbox = (value: string) => {
    const handlers: Record<string, () => void> = {
      orgAppointed: () =>
        setPagePayload({
          ...pagePayload,
          orgAppointed: !pagePayload.orgAppointed,
        }),

      freePayment: () =>
        setPagePayload({
          ...pagePayload,
          freePayment: !pagePayload.freePayment,
        }),
    }

    handlers[value]?.()
  }

  const handleExportExcel = () => {
    if (listOrgResponse && listOrgResponse.content.length > 0) {
      const csvData = listOrgResponse?.content.flatMap((row, rowIndex) =>
        row.licenses.map((license) => ({
          [t('ItherapyId')]:
            (listOrgResponse?.number || 0) * (listOrgResponse?.size || 0) +
            rowIndex +
            1,
          [t('IOrgNameShort')]: row.orgName,
          [t('IAccount')]: row.email,
          [t('ICountry')]: getCountryFromCountryCode(row.countryCode),
          [t('IProductNameSelect')]: license.goodsName,
          [t('IType')]: getLicenseMethodType(license.paymentType),
          [t(
            'IUsePeriod',
          )]: `${license.licenseActivationAt} ~ ${license.licenseDeactivationAt}`,
          ...(activeTabs === ACTIVE_PRODUCT
            ? {
                [t('IProductOperation')]: license.licenseStatus,
              }
            : {}),
          [t('IRemark')]: license.remark ? license.remark : '-',
        })),
      )
      downloadCsv(csvData)
    }
  }

  const handleSearch = (key: string, value: string) => {
    setPagePayload({
      ...pagePayload,
      searchValueType: key,
      searchValue: value,
    })
  }

  const emptyRows =
    listOrgResponse && listOrgResponse.content.length > 0 ? 0 : 5

  useEffect(() => {
    init()
  }, [t, pagePayload])

  return (
    <CardWithTitle>
      <div className={classes.tabsUnderline}>
        {LIST_TABS.map((item) => (
          <div key={item.id}>
            <Button
              onClick={() => onChangeProductStatus(item.id)}
              className={`${classes.tabUnderline} ${
                activeTabs === item.id ? classes.activeTabs : ''
              }`}
            >
              {t(item.label)}
            </Button>
            {activeTabs === item.id && <div className={classes.active} />}
          </div>
        ))}
        <div className={classes.underlineIndicator} />
      </div>
      <div className={classes.root}>
        <TableToolbar
          onChangeSelect={handleSearchSelect}
          onSearch={handleSearch}
        />
        <div className={classes.downloads}>
          <div>
            {ProductOperationAgency.map((item) => (
              <div key={item.value}>
                <Checkbox
                  checked={isCheckedCheckbox(item.value)}
                  onChange={() => onChangeCheckbox(item.value)}
                />
                <span>{t(item.label)}</span>
              </div>
            ))}
          </div>
          <div>
            <Button
              color='primary'
              variant='contained'
              onClick={handleExportExcel}
            >
              {t('IDownloadExcel')}
            </Button>
          </div>
        </div>
        {loading ? (
          <div
            style={{
              minHeight: 500,
              position: 'relative',
            }}
          >
            <AppLoading loading />
          </div>
        ) : (
          <Fragment>
            <Table size='small' className={classes.table}>
              <TableHead className={classes.tableHeader}>
                {(activeTabs === ACTIVE_PRODUCT
                  ? LIST_COLUMN
                  : LIST_COLUMN_V1
                ).map((item) => (
                  <TableCell
                    align='center'
                    padding='checkbox'
                    key={item.key}
                    style={{
                      whiteSpace: 'pre',
                    }}
                  >
                    {t(item.label)}
                  </TableCell>
                ))}
              </TableHead>
              {listOrgResponse &&
                listOrgResponse.content.map(
                  (row: OrgByLicense, rowIndex: number) => (
                    <TableBody
                      className={classes.tableBody}
                      key={row.orgId}
                      onClick={() => handleRedirectToDetail(row.orgId)}
                    >
                      {row.licenses &&
                        row.licenses.length > 0 &&
                        row.licenses.map(
                          (license: OrgByLicenseDetail, index: number) => (
                            <TableRow
                              key={license.licenseId}
                              className={classes.rowCell}
                            >
                              {index === 0 && (
                                <Fragment>
                                  <TableCell
                                    align='center'
                                    rowSpan={row.licenses.length || 1}
                                  >
                                    {listOrgResponse?.number *
                                      listOrgResponse?.size +
                                      rowIndex +
                                      1}
                                  </TableCell>
                                  <TableCell
                                    align='center'
                                    rowSpan={row.licenses.length || 1}
                                  >
                                    {row.orgName}
                                  </TableCell>
                                  <TableCell
                                    align='center'
                                    rowSpan={row.licenses.length || 1}
                                  >
                                    {row.email}
                                  </TableCell>
                                </Fragment>
                              )}

                              <TableCell align='center'>
                                {getCountryFromCountryCode(row.countryCode)}
                              </TableCell>
                              <TableCell
                                align='center'
                                style={{
                                  width: 180,
                                }}
                              >
                                {license.goodsName}
                              </TableCell>
                              <TableCell align='center'>
                                {getLicenseMethodType(license.paymentType)}
                              </TableCell>
                              <TableCell
                                align='center'
                                style={{
                                  width: 155,
                                }}
                              >
                                {`${license.licenseActivationAt} ~ ${license.licenseDeactivationAt}`}
                              </TableCell>
                              <TableCell
                                align='center'
                                onClick={(element) => {
                                  element.stopPropagation()
                                  element.preventDefault()
                                  if (
                                    license.licenseStatus !==
                                      LICENSE_STATUS.DESTROYED &&
                                    new Date(
                                      license.licenseDeactivationAt,
                                    ).getTime() > new Date().getTime()
                                  ) {
                                    setActiveDialog(true)
                                    setSelectedLicense({
                                      orgName: row.orgName,
                                      productName: license.goodsName,
                                      licenseId: license.licenseId,
                                      statusLicense: license.licenseStatus,
                                    })
                                  }
                                }}
                              >
                                {license.licenseStatus === 'ACTIVE' &&
                                new Date(
                                  license.licenseDeactivationAt,
                                ).getTime() <= new Date().getTime()
                                  ? t('ILicenseExpiration')
                                  : getLicenseStatus(license.licenseStatus)}
                              </TableCell>
                              <TableCell align='center'>
                                {license.remark}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                    </TableBody>
                  ),
                )}
              {emptyRows > 0 && (
                <TableBody>
                  <TableRow
                    className={classes.tableBody}
                    style={{
                      height: 43 * emptyRows,
                      backgroundColor: '#F9F9FB',
                    }}
                  >
                    <TableCell colSpan={14} />
                  </TableRow>
                </TableBody>
              )}
            </Table>
            {listOrgResponse && listOrgResponse?.totalPages > 0 && (
              <Pagination
                totalPageCount={listOrgResponse?.totalPages || 0}
                currentPageIndex={Number(pagePayload.page)}
                itemCountPerPage={Number(pagePayload.size)}
                setCurrentPageIndex={(page) => {
                  window.document.documentElement.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  })
                  setPagePayload({
                    ...pagePayload,
                    page,
                  })
                }}
                onItemCountPerPageChanged={(e) => {
                  window.document.documentElement.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  })
                  setPagePayload({
                    ...pagePayload,
                    size: e.target.value as number,
                    page: 0,
                  })
                }}
                loading={false}
              />
            )}
          </Fragment>
        )}

        <DialogActiveProduct
          open={activeDialog}
          selectedLicense={selectedLicense}
          onClose={() => {
            setActiveDialog(false)
          }}
          onUpdateList={init}
        />
      </div>
    </CardWithTitle>
  )
}

export default ProductOperationOrganizationList
